import { useCallback } from "react";

import { isClickableMetric } from "@/constants/metrics";
import { ICompositeMetric } from "@/stores/compositeMetric";

import MetricItemView from "./MetricItemView";

/**
 * Props for MetricItemContainer component
 */
interface MetricItemContainerProps {
  compositeMetric: ICompositeMetric;
  numCols: number;
  isSmallScreen: boolean;
  isDark: boolean;
  onNavigateToReports: () => void;
  handleTransferValueRangeClick: () => void;
  handleMetricSelect: (compositeMetric: ICompositeMetric) => void;
}

/**
 * Container component managing metric item interactions and state
 * @param root0 - Component props
 * @param root0.compositeMetric - The composite metric to display
 * @param root0.numCols - Number of columns for grid layout
 * @param root0.isSmallScreen - Whether rendering on mobile screen size
 * @param root0.isDark - Whether using dark theme styling
 * @param root0.onNavigateToReports - Callback invoked to navigate to reports
 * @param root0.handleTransferValueRangeClick - Callback invoked when transfer value metric is clicked
 * @param root0.handleMetricSelect - Callback invoked when metric is selected
 */
const MetricItemContainer = ({
  compositeMetric,
  numCols,
  isSmallScreen,
  isDark,
  onNavigateToReports,
  handleTransferValueRangeClick,
  handleMetricSelect,
}: MetricItemContainerProps) => {
  const isInteractive =
    compositeMetric.label === "Reports" ||
    compositeMetric.label === "Gemini Player Valuation" ||
    isClickableMetric(compositeMetric.label);

  const handlePress = useCallback(() => {
    if (compositeMetric.label === "Reports") {
      onNavigateToReports();
    } else if (compositeMetric.label === "Gemini Player Valuation") {
      handleTransferValueRangeClick();
    } else if (isClickableMetric(compositeMetric.label)) {
      handleMetricSelect(compositeMetric);
    }
  }, [
    compositeMetric,
    onNavigateToReports,
    handleTransferValueRangeClick,
    handleMetricSelect,
  ]);

  return (
    <MetricItemView
      compositeMetric={compositeMetric}
      numCols={numCols}
      isSmallScreen={isSmallScreen}
      isDark={isDark}
      isInteractive={isInteractive}
      onPress={handlePress}
    />
  );
};

export default MetricItemContainer;
